import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {isLoaded, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Async, Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

const riderCodes: RI.GroupRiderCode[] = [
  {
    code: 'GULBR',
    propertyName: '',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
    hidden: true,
  },
  {
    code: 'GUCTR',
    propertyName: '',
    displayName: '',
    units: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  },
  {
    code: 'GUOIR',
    propertyName: '',
    displayName: '',
    units: [],
  },
  {
    code: 'GUTIR',
    propertyName: '',
    displayName: '',
    units: [],
  },
  {
    code: 'GULTC',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
  {
    code: 'GULTCEXT+GULTC',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
  {
    code: 'GUADB',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
  {
    code: 'GUACI',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
  {
    code: 'GUPWP',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
];

type ULProps = {
  plan: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  premiumType: StringUnion;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  autoIssueEligibility: boolean;
  replacement: boolean;
  riderInformation: RI.default;
  commission: StringUnion;
  deathBenefit: StringUnion;
  adb: boolean;
  sbVolume: StringUnion;
  waiver: boolean;
  strikeLayOff: boolean;
  guac: boolean;
  chronicIllness: StringUnion;
  product: StringUnion;
  jstamp: boolean;
  packageName: StringUnion;
  packageList: PackageModel;
  gulbr: boolean;
  gultc: boolean;
  gultcext: boolean;
};
export enum GroupULType {
  accelDbLongTermCare = 'GULTC',
  accelDbTeminalIll = 'GULBR',
  accidentalDeathBenefit = 'GUADB',
  childrenTerm = 'GUCTR',
  extBenefits = 'GULTCEXT+GULTC',
  guaci50 = 'GULCIADB50',
  guaci100 = 'GULCIADB100',
  levelTermInsurance = 'GUTIR',
  otherInsuredTerm = 'GUOIR',
  payorWaiver = 'GUPWP',
}

export class InvalidGroupUL {
  public readonly key = 'InvalidGroupUL';

  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly waiver: boolean,
    public readonly strikeLayOff: boolean,
    public readonly guac: boolean,
    public readonly chronicIllness: StringUnion,
    public readonly product: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageName: StringUnion,
    public readonly packageList: PackageModel,
    public readonly gulbr: boolean,
    public readonly gultc: boolean,
    public readonly gultcext: boolean
  ) {}
}

export class ValidGroupUL {
  public static createApp({
    plan,
    premium,
    calculatedPremium,
    cwa,
    situsState,
    type,
    faceAmt,
    policyNumber,
    premiumType,
    priInsured,
    spouseInsured,
    autoIssueEligibility,
    replacement,
    riderInformation,
    commission,
    deathBenefit,
    adb,
    sbVolume,
    waiver,
    strikeLayOff,
    guac,
    chronicIllness,
    product,
    jstamp,
    packageName,
    packageList,
    gulbr,
    gultc,
    gultcext,
  }: ULProps) {
    return this.create(
      plan,
      premium,
      calculatedPremium,
      cwa,
      situsState,
      type,
      faceAmt,
      policyNumber,
      premiumType,
      priInsured,
      spouseInsured,
      autoIssueEligibility,
      replacement,
      riderInformation,
      commission,
      deathBenefit,
      adb,
      sbVolume,
      waiver,
      strikeLayOff,
      guac,
      chronicIllness,
      product,
      jstamp,
      packageName,
      packageList,
      gulbr,
      gultc,
      gultcext
    );
  }

  public static create(
    plan: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    premiumType: StringUnion,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    autoIssueEligibility: boolean,
    replacement: boolean,
    riderInformation: RI.default,
    commission: StringUnion,
    deathBenefit: StringUnion,
    adb: boolean,
    sbVolume: StringUnion,
    waiver: boolean,
    strikeLayOff: boolean,
    guac: boolean,
    chronicIllness: StringUnion,
    product: StringUnion,
    jstamp: boolean,
    packageName: StringUnion,
    packageList: PackageModel,
    gulbr: boolean,
    gultc: boolean,
    gultcext: boolean
  ) {
    return isNonEmptyString(plan) && isLoaded(packageList)
      ? new ValidGroupUL(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderInformation,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          waiver,
          strikeLayOff,
          guac,
          chronicIllness,
          product,
          jstamp,
          packageName,
          packageList,
          gulbr,
          gultc,
          gultcext
        )
      : new InvalidGroupUL(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderInformation,
          commission,
          deathBenefit,
          adb,
          sbVolume,
          waiver,
          strikeLayOff,
          guac,
          chronicIllness,
          product,
          jstamp,
          packageName,
          packageList,
          gulbr,
          gultc,
          gultcext
        );
  }

  public readonly key = 'ValidGroupUL';
  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly sbVolume: StringUnion,
    public readonly waiver: boolean,
    public readonly strikeLayOff: boolean,
    public readonly guac: boolean,
    public readonly chronicIllness: StringUnion,
    public readonly product: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageName: StringUnion,
    public readonly packageList: Loaded<object[]>,
    public readonly gulbr: boolean,
    public readonly gultc: boolean,
    public readonly gultcext: boolean
  ) {}
}

export type GroupUL = InvalidGroupUL | ValidGroupUL;

export type GroupULOptions<TReturn> = ToPartialMatchOptions<GroupUL, 'key', TReturn>;

export const matchGroupUL = <TReturn>(options: GroupULOptions<TReturn>) => (app: GroupUL): TReturn => {
  switch (app.key) {
    case 'InvalidGroupUL':
      return options.InvalidGroupUL ? options.InvalidGroupUL(app) : options.default(app);
    case 'ValidGroupUL':
      return options.ValidGroupUL ? options.ValidGroupUL(app) : options.default(app);
  }
};

export const EmptyGroupUL = new InvalidGroupUL(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  false,
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new UnassignedPackageModel(),
  false,
  false,
  false
);

export const changePlan = (plan: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    riderInformation: new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  });
export const changePremium = (premium: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSitusState = (situsState: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, type: NonEmptyString.create(type)});
export const changeFaceAmt = (faceAmt: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePremiumType = (preType: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changePriInsured = (priInsured: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, autoIssueEligibility});
export const changeReplacement = (replacement: boolean) => (app: GroupUL) =>
  ValidGroupUL.createApp({
    ...app,
    replacement,
    commission: !replacement
      ? new EmptyString()
      : app.commission instanceof EmptyString
      ? NonEmptyString.create('First Year Commissions')
      : app.commission,
  });
export const changeCommision = (commission: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, commission: NonEmptyString.create(commission)});
export const changeDeathBenefit = (deathBenefit: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, deathBenefit: NonEmptyString.create(deathBenefit)});
export const changeSBVolume = (vol: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, sbVolume: NonEmptyString.create(vol)});
export const changeChronicIllness = (illness: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, chronicIllness: NonEmptyString.create(illness)});
export const changeADB = (adb: boolean) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, adb, sbVolume: new EmptyString()});
export const changeWaiver = (waiver: boolean) => (app: GroupUL) => ValidGroupUL.createApp({...app, waiver});
export const changeStrikeLayOff = (strikeLayOff: boolean) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, strikeLayOff});
export const changeGUAC = (guac: boolean) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, guac, chronicIllness: new EmptyString()});
export const changeGULBR = (gulbr: boolean) => (app: GroupUL) => ValidGroupUL.createApp({...app, gulbr});
export const changeGULTC = (gultc: boolean) => (app: GroupUL) => ValidGroupUL.createApp({...app, gultc});
export const changeGULTCEXT = (gultcext: boolean) => (app: GroupUL) => ValidGroupUL.createApp({...app, gultcext});
export const changeproduct = (product: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, product: NonEmptyString.create(product)});
export const changeJStamp = (jstamp: boolean) => (app: GroupUL) => ValidGroupUL.createApp({...app, jstamp});
export const changePackageName = (packageName: string) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, packageName: NonEmptyString.create(packageName)});
export const changePackageList = (packageList: Async<object[]>) => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, packageList});
export const changePackageItem = (packageItem: object) => (app: GroupUL) =>
  ValidGroupUL.createApp({
    ...app,
    strikeLayOff: packageItem['strikeWaiver'] !== null && Number(packageItem['strikeWaiver']) === 0,
    waiver:
      app.plan.value === 'GUL23'
        ? packageItem['payorWaiver'] !== null && Number(packageItem['payorWaiver']) === 0
        : packageItem['premiumWaiver'] !== null && Number(packageItem['premiumWaiver']) === 0,
    adb: packageItem['accidentalDeathBenefit'] !== null && Number(packageItem['accidentalDeathBenefit']) === 0,
    deathBenefit: NonEmptyString.create('Option 1'),
  });

export const resetSpouseInsured = () => (app: GroupUL) =>
  ValidGroupUL.createApp({...app, spouseInsured: new EmptyString()});

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: GroupUL) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidGroupUL.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const planList = [
  {name: 'GUL23', displayName: 'GUL23SI'},
  {name: 'SUL23', displayName: 'GUL23CGI'},
  {name: 'GIUL23', displayName: 'GUL23GI'},
];

export const isSpouse = (type: string) => {
  return type === 'Spouse';
};
